import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Leaf, Shield, Truck, ChevronRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useMushrooms } from "../context/MushroomsContext";
import PodcastSection from "../components/PodcastSection";
import { useLang } from "../context/LanguageContext";
import RecipeDetailModal from "../components/RecipeDetailModal";

const FEATURED_IDS = ["shiitake", "oyster", "lions-mane", "portobello"];

const HOME_RECIPES = [
  {
    name: "Butter Garlic Stir-fry",
    time: "10 mins",
    difficulty: "Easy",
    image: "https://images.unsplash.com/photo-1676140382464-83cd80afc22b?auto=format&fit=crop&w=600&q=80",
    tag: "Quick & Easy",
    description: "Quick pan-toss with garlic and herbs — ready in 10 minutes.",
    ingredients: ["300g button mushrooms", "3 tbsp butter", "4 garlic cloves", "Fresh parsley", "Salt and pepper"],
    steps: ["Melt butter in a hot pan over medium-high heat.", "Add garlic, sauté for 1 minute until fragrant.", "Add mushrooms in a single layer — cook undisturbed 3 mins.", "Toss and cook 2 more mins until golden.", "Season well, garnish with chopped parsley and serve hot."],
  },
  {
    name: "Oyster Mushroom Masala",
    time: "30 mins",
    difficulty: "Medium",
    image: "https://images.unsplash.com/photo-1643381910338-0677d1255e91?auto=format&fit=crop&w=600&q=80",
    tag: "Indian Flavors",
    description: "Spicy Indian curry with a rich tomato-onion base.",
    ingredients: ["250g oyster mushrooms", "2 ripe tomatoes, pureed", "1 large onion, finely chopped", "1 tsp ginger-garlic paste", "1 tsp cumin, 1 tsp coriander, ½ tsp chilli powder", "2 tbsp oil", "Fresh coriander to garnish"],
    steps: ["Heat oil and sauté onion until golden brown.", "Add ginger-garlic paste, cook 1 min.", "Add tomato puree and spices; cook until oil separates.", "Tear oyster mushrooms by hand and add to masala.", "Cook on medium heat 8 mins; garnish with fresh coriander."],
  },
  {
    name: "Shiitake Miso Ramen",
    time: "45 mins",
    difficulty: "Medium",
    image: "https://images.unsplash.com/photo-1614886332795-6ef15400ee2d?auto=format&fit=crop&w=600&q=80",
    tag: "Asian Fusion",
    description: "Deep umami ramen broth with silky shiitake and miso.",
    ingredients: ["200g fresh shiitake mushrooms", "2 tbsp white miso paste", "1L dashi or vegetable stock", "2 portions ramen noodles", "2 soft-boiled eggs", "Spring onions, sliced", "1 tsp sesame oil"],
    steps: ["Simmer shiitake mushrooms in dashi stock for 20 mins.", "Remove from heat; dissolve miso in broth — do not boil.", "Cook ramen noodles per packet instructions.", "Arrange noodles in bowls; pour hot miso broth over.", "Top with shiitake, halved eggs, spring onions and sesame oil."],
  },
];

function BadgeChip({ badge }) {
  if (!badge) return null;
  const key = badge.toLowerCase().replace(/\s+/g, '-').replace("'", "");
  return <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full badge-${key}`}>{badge}</span>;
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }
});

export default function HomePage() {
  const { addToCart } = useCart();
  const { mushrooms } = useMushrooms();
  const { t, lang } = useLang();
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const featured = mushrooms.filter(m => FEATURED_IDS.includes(m.id)).slice(0, 4);

  return (
    <>
    <div className="min-h-screen" style={{ backgroundColor: 'var(--c-bg)' }}>

      {/* ── Hero ── */}
      <section className="relative pt-16 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1512595594595-82b7f049313b?auto=format&fit=crop&w=1600&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'var(--c-hero-overlay)' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-28 text-center">
          <motion.div {...fadeUp(0.1)} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 border" style={{ backgroundColor: 'rgba(217,122,70,0.10)', borderColor: 'rgba(217,122,70,0.30)', color: '#D97A46' }}>
            <Leaf className="w-3 h-3" /> {t('hero.badge')}
          </motion.div>
          <motion.h1 {...fadeUp(0.2)} className="font-serif text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight leading-none mb-5" style={{ color: 'var(--c-text)' }} data-testid="hero-heading">
            {t('hero.title')}
          </motion.h1>
          <motion.p {...fadeUp(0.32)} className="text-lg max-w-xl mx-auto mb-8 leading-relaxed" style={{ color: 'var(--c-muted)' }}>
            {t('hero.subtitle')}
          </motion.p>
          <motion.div {...fadeUp(0.44)} className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/shop" data-testid="hero-shop-now-btn">
              <button className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg" style={{ backgroundColor: '#D97A46' }}>
                {t('hero.shopNow')} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <a href="#featured" data-testid="hero-explore-btn">
              <button className="flex items-center gap-2 px-7 py-3.5 rounded-full font-medium transition-all hover:-translate-y-0.5 border" style={{ color: 'var(--c-text)', borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)' }}>
                {t('hero.explore')} <ChevronRight className="w-4 h-4" />
              </button>
            </a>          </motion.div>
        </div>
      </section>

      {/* ── Trust Badges ── */}
      <section className="py-5 border-y" style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x" style={{ '--tw-divide-color': 'var(--c-border)' }}>
          {[
            { icon: Leaf, textKey: 'trust.organic', subKey: 'trust.organicSub' },
            { icon: Truck, textKey: 'trust.delivery', subKey: 'trust.deliverySub' },
            { icon: Shield, textKey: 'trust.quality', subKey: 'trust.qualitySub' },
          ].map(({ icon: Icon, textKey, subKey }) => (
            <div key={textKey} className="flex items-center gap-3 py-3 sm:py-0 sm:px-6 first:pt-0 last:pb-0 sm:first:pl-0 sm:last:pr-0">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(217,122,70,0.10)' }}>
                <Icon className="w-4 h-4" style={{ color: '#D97A46' }} />
              </div>
              <div>
                <p className="text-sm font-semibold leading-tight" style={{ color: 'var(--c-text)' }}>{t(textKey)}</p>
                <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{t(subKey)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Featured Varieties ── */}
      <section id="featured" className="py-14 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: 'var(--c-bg)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-10">
            <div>
              <p className="text-xs font-semibold tracking-widest uppercase mb-1.5" style={{ color: '#D97A46' }}>{t('featured.label')}</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold" style={{ color: 'var(--c-text)' }}>{t('featured.title')}</h2>
            </div>
            <Link to="/shop" className="hidden sm:flex items-center gap-1.5 text-sm font-medium transition-colors hover:underline" style={{ color: '#D97A46' }} data-testid="see-all-link">
              {t('featured.seeAll')} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {featured.map((mushroom, i) => (
              <motion.div key={mushroom.id}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="group rounded-2xl overflow-hidden flex flex-col hover:-translate-y-1.5 transition-transform duration-300"
                style={{ backgroundColor: 'var(--c-surface)', boxShadow: 'var(--c-shadow-lg)', border: '1px solid var(--c-border)' }}
                data-testid={`featured-card-${mushroom.id}`}
              >
                <Link to={`/mushroom/${mushroom.id}`} className="relative h-52 overflow-hidden">
                  <img src={mushroom.image} alt={mushroom.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  {mushroom.badge && <div className="absolute top-3 left-3"><BadgeChip badge={mushroom.badge} /></div>}
                  {mushroom.isUpcoming && (
                    <div className="absolute top-3 right-3">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">{t('featured.upcoming')}</span>
                    </div>
                  )}
                </Link>
                <div className="p-5 flex flex-col gap-3 flex-1">
                  <div>
                    <Link to={`/mushroom/${mushroom.id}`}>
                      <h3 className="font-serif text-xl font-semibold hover:text-[#D97A46] transition-colors leading-tight" style={{ color: 'var(--c-text)' }}>{mushroom.name}</h3>
                    </Link>
                    <p className="text-xs italic mt-0.5" style={{ color: 'var(--c-muted)' }}>{mushroom.scientificName}</p>
                  </div>
                  <p className="text-sm leading-relaxed flex-1 line-clamp-2" style={{ color: 'var(--c-muted)' }}>
                    {(lang === 'hi' && mushroom.description_hi) ? mushroom.description_hi : mushroom.description}
                  </p>
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      <span className="text-lg font-semibold" style={{ color: '#D97A46' }}>Rs.{mushroom.price}</span>
                      <span className="text-xs ml-1" style={{ color: 'var(--c-muted)' }}>/ {mushroom.uom || "250g"}</span>
                    </div>
                    {!mushroom.isUpcoming ? (
                      <button data-testid={`add-to-cart-${mushroom.id}`} onClick={() => addToCart(mushroom)}
                        className="px-4 py-2 rounded-full text-sm font-medium text-white transition-all hover:shadow-md hover:-translate-y-0.5"
                        style={{ backgroundColor: '#D97A46' }}>
                        {t('featured.addToCart')}
                      </button>
                    ) : (
                      <Link to={`/mushroom/${mushroom.id}`}>
                        <span className="px-3 py-1.5 rounded-full text-xs font-medium text-amber-700 border border-amber-200 bg-amber-50">{t('featured.preOrder')}</span>
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Link to="/shop" data-testid="mobile-see-all-link">
              <button className="px-6 py-3 border rounded-full text-sm font-medium transition-all hover:-translate-y-0.5" style={{ borderColor: 'var(--c-border)', color: 'var(--c-text)', backgroundColor: 'var(--c-surface)' }}>
                {t('home.viewAllVarieties')}
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Recipes Teaser ── */}
      <section id="recipes" className="py-14 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: 'var(--c-surface-alt)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs font-semibold tracking-widest uppercase mb-1.5" style={{ color: '#D97A46' }}>{t('home.inKitchen')}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold" style={{ color: 'var(--c-text)' }}>{t('home.inspiredRecipes')}</h2>
            <p className="text-base mt-2 max-w-md mx-auto" style={{ color: 'var(--c-muted)' }}>{t('home.recipesSub')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {HOME_RECIPES.map((r, i) => (
              <motion.div key={r.name} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                onClick={() => setSelectedRecipe(r)}
                className="group relative h-60 rounded-2xl overflow-hidden cursor-pointer" data-testid={`recipe-card-${i}`}>
                <img src={r.image} alt={r.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(26,47,36,0.88) 0%, rgba(26,47,36,0.2) 60%, transparent 100%)' }} />
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: '#D97A46' }}>{r.tag}</span>
                  <h3 className="font-serif text-lg font-semibold text-white mt-0.5">{r.name}</h3>
                  <p className="text-xs text-white/70">{r.time}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Podcast Section ── */}
      <PodcastSection />

      {/* ── About ── */}
      <section id="about" className="py-14 sm:py-20 px-4 sm:px-6" style={{ backgroundColor: 'var(--c-bg)' }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest uppercase mb-2" style={{ color: '#D97A46' }}>{t('home.storyLabel')}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-bold leading-tight" style={{ color: 'var(--c-text)' }}>{t('home.storyTitle')}</h2>
            <p className="text-base mt-3 max-w-xl mx-auto" style={{ color: 'var(--c-muted)' }}>{t('home.storySub')}</p>
          </div>

          {/* 4 quality pillars */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-14">
            {[
              { num: "01", titleKey: "story.pillar1.title", bodyKey: "story.pillar1.body" },
              { num: "02", titleKey: "story.pillar2.title", bodyKey: "story.pillar2.body" },
              { num: "03", titleKey: "story.pillar3.title", bodyKey: "story.pillar3.body" },
              { num: "04", titleKey: "story.pillar4.title", bodyKey: "story.pillar4.body" },
            ].map((p, i) => (
              <motion.div key={p.num}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="p-6 rounded-2xl border flex gap-4" style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)', boxShadow: 'var(--c-shadow-sm)' }}>
                <span className="font-serif text-3xl font-bold leading-none flex-shrink-0 mt-0.5" style={{ color: '#D97A46' }}>{p.num}</span>
                <div>
                  <h3 className="font-semibold text-base mb-2 leading-snug" style={{ color: 'var(--c-text)' }}>{t(p.titleKey)}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--c-muted)' }}>{t(p.bodyKey)}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[["8+", t('stats.varieties')], ["Zero", t('stats.pesticides')], ["100%", t('stats.graded')], ["24h", t('stats.delivery')]].map(([num, label]) => (
              <div key={label} className="text-center p-4 rounded-2xl border" style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)' }}>
                <p className="font-serif text-2xl font-bold" style={{ color: '#D97A46' }}>{num}</p>
                <p className="text-xs mt-1 leading-tight" style={{ color: 'var(--c-muted)' }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Production Unit Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="mt-10 p-6 rounded-2xl border flex flex-col sm:flex-row items-start sm:items-center gap-6"
            style={{ backgroundColor: 'var(--c-surface)', borderColor: 'var(--c-border)' }}
          >
            <div className="flex-1">
              <p className="text-xs font-semibold tracking-widest uppercase mb-1" style={{ color: '#D97A46' }}>{t('about.productionUnit')}</p>
              <p className="font-semibold text-base leading-snug" style={{ color: 'var(--c-text)' }}>PIIKK Agro Farm Fresh Mushrooms</p>
              <p className="text-sm mt-1 leading-relaxed" style={{ color: 'var(--c-muted)' }}>
                Bharlai, Shivpur, Panch Koshi Road<br />
                Near Sampada Residency<br />
                Varanasi – 221003
              </p>
            </div>
            <a
              href="https://wa.me/916362516143"
              target="_blank" rel="noreferrer"
              className="flex items-center gap-2.5 px-5 py-3 rounded-full font-semibold text-white text-sm transition-all hover:-translate-y-0.5 hover:shadow-md flex-shrink-0"
              style={{ backgroundColor: '#25D366' }}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              +91 63625 16143
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 px-4 sm:px-6 border-t" style={{ borderColor: 'var(--c-border)', backgroundColor: 'var(--c-surface)' }}>
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="font-serif text-xl font-bold" style={{ color: 'var(--c-text)' }}>PIIKK</span>
            <span className="text-xs font-medium tracking-widest uppercase" style={{ color: '#D97A46' }}>Mushrooms</span>
          </div>
          <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{t('footer.whatsapp')}</p>
          <p className="text-xs" style={{ color: 'var(--c-muted)' }}>{t('footer.copyright')}</p>
        </div>
      </footer>

    </div>

      {selectedRecipe && (
        <RecipeDetailModal
          recipe={selectedRecipe}
          mushroomName={selectedRecipe.tag}
          onClose={() => setSelectedRecipe(null)}
        />
      )}
    </>
  );
}
